import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import BlizzardIcon from 'app/frontend/base/components/BlizzardIcon'
import CharacterClassIcon from 'app/frontend/base/components/CharacterClassIcon'
import { useTeam } from 'app/frontend/base/config/context/team'
import TeamWishlistCharacterMetadata from 'app/frontend/types/custom/TeamWishlistCharacterMetadata'
import dayjs from 'dayjs'
import { orderBy } from 'lodash'
import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'
import RelativeTime from 'dayjs/plugin/relativeTime'
dayjs.extend(RelativeTime)

type Props = {
  teamWishlist?: wowaudit.TeamWishlist
  open: boolean
  setOpen: (open: boolean) => void
}

const TeamAgeModal = ({ teamWishlist, open, setOpen }: Props) => {
  const { team } = useTeam()

  const sortedSpecsWithDifference = (
    character: TeamWishlistCharacterMetadata,
  ) => {
    const specData = character.specs.map((spec) => {
      const secondsSinceEpoch = spec.age ? dayjs(spec.age).unix() : 0
      const difference = dayjs().diff(dayjs(spec.age), 'day')

      return {
        ...spec,
        difference,
        secondsSinceEpoch,
      }
    })

    return orderBy(specData, (s) => s.secondsSinceEpoch, 'desc')
  }

  const withSortedSpecs = useMemo(
    () =>
      orderBy(
        teamWishlist?.metadata.map((character) => {
          return {
            ...character,
            specs: sortedSpecsWithDifference(character),
          }
        }),
        (c) => c.specs[0].secondsSinceEpoch,
        'desc',
      ),
    [teamWishlist?.metadata],
  )

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className='max-w-3xl'>
        <DialogHeader>
          <DialogTitle>Time since last update</DialogTitle>
        </DialogHeader>
        <table>
          <tbody>
            {withSortedSpecs.map((character, i) => (
              <tr key={i}>
                <td>
                  <div className='flex items-center gap-1.5 py-px'>
                    <CharacterClassIcon characterClass={character.class} />
                    <Link
                      to={`${team.path}/loot/characters/${character.id}`}
                      className='hover:underline'
                    >
                      {character.name}
                    </Link>
                  </div>
                </td>
                {character.specs.map((spec, i) => {
                  return (
                    <td key={i}>
                      <div className='flex items-center gap-2'>
                        <BlizzardIcon iconName={spec.icon} />
                        {React.createElement(
                          spec.report_url ? `a` : `span`,
                          {
                            className: `${spec.report_url ? 'underline' : ''} ${
                              spec.age
                                ? spec.difference > 21
                                  ? 'text-destructive-foreground'
                                  : spec.difference > 7
                                  ? 'text-warning-foreground'
                                  : 'text-success-foreground'
                                : 'opacity-50 italic'
                            }`,
                            href: spec.report_url,
                            target: '_blank',
                            rel: 'noreferrer',
                          },
                          spec.age ? dayjs(spec.age).fromNow() : 'never',
                        )}
                      </div>
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </DialogContent>
    </Dialog>
  )
}

export default TeamAgeModal
